/** Faecher und Einstellungen der Schulseite (F-22). */
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { stundeAusTermin, type Stunde } from '@/domain/school/timetable';
import { bundeslandAus } from '@/lib/holidays/de';
import type { Bundesland, CalendarEvent, Grade, SchoolSettings, Subject, Task } from '@/domain/types';

/** Aktive Faecher nach Reihenfolge; mit `alle` auch archivierte. */
export function useSubjects(alle = false): Subject[] {
  const rows = useLiveQuery(() => db.subjects.orderBy('order').toArray(), [], [] as Subject[]);
  return alle ? rows : rows.filter((s) => !s.archived);
}

/** `undefined`, solange die Schulseite noch nie geoeffnet wurde. */
export function useSchoolSettings(): SchoolSettings | undefined {
  return useLiveQuery(() => db.schoolSettings.get('schule'), []);
}

/** Bundesland fuer die Feiertage (F-41) - ohne Einstellung Brandenburg. */
export function useBundesland(): Bundesland {
  return bundeslandAus(useSchoolSettings()?.bundesland);
}

/** Alle Stunden - aus den Serienterminen mit Fach. */
export function useStunden(settings: SchoolSettings | undefined): Stunde[] {
  const events = useLiveQuery(() => db.events.filter((e) => !!e.subjectId && !e.recurringEventId).toArray(), [], []);
  if (!settings) return [];
  return events.map((e) => stundeAusTermin(e, settings.aWocheAb)).filter((s): s is Stunde => s !== null);
}

/** Alle Klausuren (Termine mit `klausur`), nach Datum. */
export function useKlausuren(): CalendarEvent[] {
  const rows = useLiveQuery(() => db.events.filter((e) => !!e.klausur).toArray(), [], [] as CalendarEvent[]);
  return [...rows].sort((a, b) => String(a.start).localeCompare(String(b.start)));
}

/** Lernplan-Eintraege je Klausur, nach Tag. */
export function useLernplan(): Map<string, Task[]> {
  const rows = useLiveQuery(() => db.tasks.filter((t) => !!t.examId).toArray(), [], [] as Task[]);
  const map = new Map<string, Task[]>();
  for (const t of [...rows].sort((a, b) => (a.plannedDate ?? '').localeCompare(b.plannedDate ?? '') || a.order - b.order)) {
    const liste = map.get(t.examId!) ?? [];
    liste.push(t);
    map.set(t.examId!, liste);
  }
  return map;
}

/** Alle Noten, neueste zuerst. */
export function useNoten(): Grade[] {
  const rows = useLiveQuery(() => db.grades.toArray(), [], [] as Grade[]);
  return [...rows].sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
}

/** Hausaufgaben: Aufgaben mit Fach - ohne Lernplan-Eintraege (die gehoeren zur Klausur). */
export function useHausaufgaben(): Task[] {
  return useLiveQuery(() => db.tasks.filter((t) => !!t.subjectId && !t.examId).toArray(), [], [] as Task[]);
}

/** Hausaufgaben nach Faelligkeit, ohne Datum ans Ende. */
export const nachFrist = (a: Task, b: Task) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') || a.order - b.order;
